import React, { useState, useRef, useEffect, useCallback } from 'react';
import { Outlet, NavLink, useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { useTheme } from '../context/ThemeContext';
import api from '../services/api';
import BackToTop from './BackToTop';
import {
  HomeIcon, ArrowUpTrayIcon, ChartBarIcon,
  UserCircleIcon, ArrowRightOnRectangleIcon, Bars3Icon, XMarkIcon,
  ClipboardDocumentListIcon, BeakerIcon, ShieldCheckIcon,
  SunIcon, MoonIcon, QuestionMarkCircleIcon, PlayCircleIcon,
  CreditCardIcon,
} from '@heroicons/react/24/outline';

const navItems = [
  { to: '/app/dashboard', icon: HomeIcon, label: 'Dashboard' },
  { to: '/app/upload', icon: ArrowUpTrayIcon, label: 'Upload Report' },
  { to: '/app/trends', icon: ChartBarIcon, label: 'My Trends' },
  { to: '/app/tutorials', icon: PlayCircleIcon, label: 'Tutorials' },
];

const premiumNavItems = [
  { to: '/app/appointment-prep', icon: ClipboardDocumentListIcon, label: 'Appointment Prep' },
  { to: '/app/medication-check', icon: BeakerIcon, label: 'Medication Check' },
  { to: '/app/care-gaps', icon: ShieldCheckIcon, label: 'Care Gaps' },
];

const bottomNavItems = [
  { to: '/app/subscription', icon: CreditCardIcon, label: 'My Subscription' },
  { to: '/app/help', icon: QuestionMarkCircleIcon, label: 'Help & FAQ' },
  { to: '/app/profile', icon: UserCircleIcon, label: 'Profile & Settings' },
];

const SCREEN_LABELS = {
  '/app/dashboard':         'Dashboard',
  '/app/upload':            'Upload Report',
  '/app/trends':            'Trends',
  '/app/profile':           'Profile & Settings',
  '/app/appointment-prep':  'Appointment Prep',
  '/app/medication-check':  'Medication Check',
  '/app/care-gaps':         'Care Gaps',
  '/app/help':              'Help & FAQ',
  '/app/tutorials':         'Tutorials',
  '/app/subscription':      'My Subscription',
};

function screenLabel(pathname) {
  // Exact match first, then prefix (e.g. /app/analysis/123 → Viewing Analysis)
  if (SCREEN_LABELS[pathname]) return SCREEN_LABELS[pathname];
  if (pathname.startsWith('/app/analysis')) return 'Viewing Analysis';
  return 'App';
}

// Dismissible tutorial hint banner — shown once per session on non-tutorial pages
const TUTORIAL_DISMISSED_KEY = 'hcai-tutorial-hint-dismissed';
function TutorialHintBanner() {
  const location = useLocation();
  const navigate = useNavigate();
  const [dismissed, setDismissed] = useState(
    () => sessionStorage.getItem(TUTORIAL_DISMISSED_KEY) === '1',
  );

  if (dismissed) return null;
  if (location.pathname === '/app/tutorials') return null;

  const dismiss = () => {
    sessionStorage.setItem(TUTORIAL_DISMISSED_KEY, '1');
    setDismissed(true);
  };

  return (
    <div className="bg-sky-50 dark:bg-sky-900/30 border-b border-sky-100 dark:border-sky-800 px-4 py-2 flex items-center gap-3">
      <PlayCircleIcon className="w-4 h-4 text-sky-500 flex-shrink-0" />
      <p className="text-sm text-sky-700 dark:text-sky-300 flex-1">
        <strong>New to HealthClear AI?</strong> Check out our interactive{' '}
        <button
          onClick={() => { navigate('/app/tutorials'); dismiss(); }}
          className="underline font-semibold hover:text-sky-900 dark:hover:text-sky-100"
        >
          Tutorials
        </button>{' '}
        for step-by-step guides on every feature.
      </p>
      <button
        onClick={dismiss}
        className="text-sky-400 hover:text-sky-600 dark:hover:text-sky-200 text-lg leading-none flex-shrink-0"
        aria-label="Dismiss"
      >
        ×
      </button>
    </div>
  );
}

export default function Layout() {
  const { user, logout } = useAuth();
  const { dark, toggle: toggleDark } = useTheme();
  const navigate = useNavigate();
  const location = useLocation();
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const mainRef = useRef(null);

  const sendHeartbeat = useCallback((pathname) => {
    api.post('/activity/heartbeat', { screen: `Web — ${screenLabel(pathname)}` })
      .catch((err) => console.warn('[heartbeat]', err?.response?.status, err?.response?.data || err?.message));
  }, []);

  // Fire on every route change
  useEffect(() => {
    sendHeartbeat(location.pathname);
  }, [location.pathname, sendHeartbeat]);

  // Keep-alive every 30 seconds
  useEffect(() => {
    const id = setInterval(() => sendHeartbeat(location.pathname), 30_000);
    return () => clearInterval(id);
  }, [location.pathname, sendHeartbeat]);

  const handleLogout = () => { logout(); navigate('/'); };

  const Sidebar = ({ mobile = false }) => (
    <div className={`flex flex-col h-full border-r ${mobile ? 'w-72' : 'w-64'} bg-white dark:bg-slate-900 border-slate-200 dark:border-slate-700`}>
      {/* Logo */}
      <div className="flex items-center px-5 py-4 border-b border-slate-100 dark:border-slate-700">
        <img src={dark ? '/logo-dark.svg?v=3' : '/logo.svg?v=3'} alt="HealthClear AI" className="h-8 w-auto" />
        {mobile && (
          <button onClick={() => setSidebarOpen(false)} className="ml-auto text-slate-400 hover:text-slate-600 dark:hover:text-slate-200">
            <XMarkIcon className="w-5 h-5" />
          </button>
        )}
      </div>

      {/* Nav */}
      <nav className="flex-1 px-3 py-4 space-y-1 overflow-y-auto">
        {navItems.map(({ to, icon: Icon, label }) => (
          <NavLink
            key={to}
            to={to}
            onClick={() => setSidebarOpen(false)}
            className={({ isActive }) =>
              `flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm font-medium transition-colors ${
                isActive
                  ? 'bg-sky-50 text-sky-700 dark:bg-sky-900/40 dark:text-sky-300'
                  : 'text-slate-600 hover:bg-slate-50 hover:text-slate-800 dark:text-slate-400 dark:hover:bg-slate-800 dark:hover:text-slate-100'
              }`
            }
          >
            <Icon className="w-5 h-5 flex-shrink-0" />
            {label}
          </NavLink>
        ))}

        {/* Premium section */}
        {user?.subscriptionStatus === 'active' && (
          <>
            <div className="pt-3 pb-1 px-3">
              <p className="text-xs font-semibold text-slate-400 dark:text-slate-500 uppercase tracking-widest">Premium</p>
            </div>
            {premiumNavItems.map(({ to, icon: Icon, label }) => (
              <NavLink
                key={to}
                to={to}
                onClick={() => setSidebarOpen(false)}
                className={({ isActive }) =>
                  `flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm font-medium transition-colors ${
                    isActive
                      ? 'bg-sky-50 text-sky-700 dark:bg-sky-900/40 dark:text-sky-300'
                      : 'text-slate-600 hover:bg-slate-50 hover:text-slate-800 dark:text-slate-400 dark:hover:bg-slate-800 dark:hover:text-slate-100'
                  }`
                }
              >
                <Icon className="w-5 h-5 flex-shrink-0" />
                {label}
              </NavLink>
            ))}
          </>
        )}

        <div className="pt-2">
          {bottomNavItems.map(({ to, icon: Icon, label }) => (
            <NavLink
              key={to}
              to={to}
              onClick={() => setSidebarOpen(false)}
              className={({ isActive }) =>
                `flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm font-medium transition-colors ${
                  isActive
                    ? 'bg-sky-50 text-sky-700 dark:bg-sky-900/40 dark:text-sky-300'
                    : 'text-slate-600 hover:bg-slate-50 hover:text-slate-800 dark:text-slate-400 dark:hover:bg-slate-800 dark:hover:text-slate-100'
                }`
              }
            >
              <Icon className="w-5 h-5 flex-shrink-0" />
              {label}
            </NavLink>
          ))}
        </div>
      </nav>

      {/* User footer */}
      <div className="px-3 pb-4 border-t border-slate-100 dark:border-slate-700 pt-3 space-y-1">
        <div className="flex items-center gap-3 px-3 py-2">
          <div className="w-8 h-8 rounded-full bg-sky-100 dark:bg-sky-900/50 flex items-center justify-center text-sky-700 dark:text-sky-300 font-semibold text-sm">
            {user?.name?.[0]?.toUpperCase() || 'U'}
          </div>
          <div className="min-w-0">
            <p className="text-sm font-medium text-slate-700 dark:text-slate-200 truncate">{user?.name}</p>
            <p className="text-xs text-slate-400 dark:text-slate-500 truncate">{user?.email}</p>
          </div>
        </div>

        {/* Dark mode toggle */}
        <button
          onClick={toggleDark}
          title={dark ? 'Switch to light mode' : 'Switch to dark mode'}
          className="flex items-center gap-2 w-full px-3 py-2 text-sm text-slate-500 dark:text-slate-400 hover:text-slate-800 dark:hover:text-slate-100 hover:bg-slate-50 dark:hover:bg-slate-800 rounded-lg transition-colors"
        >
          {dark ? <SunIcon className="w-4 h-4" /> : <MoonIcon className="w-4 h-4" />}
          {dark ? 'Light mode' : 'Dark mode'}
        </button>

        <button
          onClick={handleLogout}
          className="flex items-center gap-2 w-full px-3 py-2 text-sm text-slate-500 dark:text-slate-400 hover:text-red-600 dark:hover:text-red-400 hover:bg-red-50 dark:hover:bg-red-900/20 rounded-lg transition-colors"
        >
          <ArrowRightOnRectangleIcon className="w-4 h-4" />
          Sign out
        </button>
      </div>
    </div>
  );

  return (
    <div className="flex h-screen bg-slate-50 dark:bg-slate-950">
      {/* Desktop sidebar */}
      <div className="hidden md:flex flex-shrink-0">
        <Sidebar />
      </div>

      {/* Mobile sidebar overlay */}
      {sidebarOpen && (
        <div className="fixed inset-0 z-50 flex md:hidden">
          <div className="fixed inset-0 bg-black/40" onClick={() => setSidebarOpen(false)} />
          <div className="relative z-10">
            <Sidebar mobile />
          </div>
        </div>
      )}

      {/* Main content */}
      <div className="flex-1 flex flex-col min-w-0 overflow-hidden">
        {/* Mobile header */}
        <div className="md:hidden flex items-center px-4 py-3 bg-white dark:bg-slate-900 border-b border-slate-200 dark:border-slate-700">
          <button onClick={() => setSidebarOpen(true)} className="text-slate-500 dark:text-slate-400 hover:text-slate-700 dark:hover:text-slate-100">
            <Bars3Icon className="w-6 h-6" />
          </button>
          <span className="ml-3 font-semibold text-slate-800 dark:text-slate-100">HealthClear AI</span>
          <button
            onClick={toggleDark}
            title={dark ? 'Switch to light mode' : 'Switch to dark mode'}
            className="ml-auto text-slate-400 dark:text-slate-500 hover:text-slate-700 dark:hover:text-slate-200 transition-colors"
          >
            {dark ? <SunIcon className="w-5 h-5" /> : <MoonIcon className="w-5 h-5" />}
          </button>
        </div>

        <main ref={mainRef} className="flex-1 overflow-y-auto bg-white dark:bg-slate-950">
          <TutorialHintBanner />
          <Outlet />
          <BackToTop scrollRef={mainRef} />
          <footer className="border-t border-slate-100 dark:border-slate-800 py-4 text-center">
            <p className="text-xs text-slate-400 dark:text-slate-600">© 2026 HealthClear AI. All rights reserved.</p>
          </footer>
        </main>
      </div>
    </div>
  );
}
